// src/pages/EditProfilePage.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import api from "../../api/Client";
import { SecureStorage } from "../../storage/SecureStorage";

// 👉 Importamos validadores comunes
import {
  validateNombre,
  validateApellidos,
  validateTelefono,
  validateCodigoPostal,
  validateDni,
} from "../../components/validator";
import { fetchUserProfile } from "../../api/User";
import { API_BASE } from "../../config";

// Función para convertir fecha de BD (YYYY-MM-DD) a formato input date
function formatDateForInput(dateString: string): string {
  if (!dateString) return "";
  
  // Si ya está en formato correcto (YYYY-MM-DD), devolver tal como está
  if (dateString.includes("-") && dateString.length === 10) {
    return dateString;
  }
  
  // Si está en formato DDMMYYYY (8 caracteres) - formato legacy
  if (dateString.length === 8) {
    const day = dateString.substring(0, 2);
    const month = dateString.substring(2, 4);
    const year = dateString.substring(4, 8);
    return `${year}-${month}-${day}`;
  }
  
  // Si viene con formato completo de timestamp, extraer solo la fecha
  const dateOnly = dateString.slice(0, 10);
  if (dateOnly.includes("-")) {
    return dateOnly;
  }
  
  console.warn("Formato de fecha no reconocido:", dateString);
  return "";
}

// Función para mantener fecha en formato YYYY-MM-DD para el servidor
function formatDateForServer(dateString: string): string {
  if (!dateString) return "";
  
  try {
    // Si ya está en formato YYYY-MM-DD, devolverla tal como está
    if (dateString.includes("-") && dateString.length === 10) {
      return dateString;
    }
    
    // Si por alguna razón viene en formato DDMMYYYY, convertir a YYYY-MM-DD
    if (dateString.length === 8) {
      const day = dateString.substring(0, 2);
      const month = dateString.substring(2, 4);
      const year = dateString.substring(4, 8);
      return `${year}-${month}-${day}`;
    }
    
    return dateString;
  } catch (error) {
    console.error("Error al formatear fecha para servidor:", dateString, error);
    return "";
  }
}

export default function EditProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [user, setUser] = useState<any>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [sex, setSex] = useState("");
  const [phone, setPhone] = useState("");
  const [postAddress, setPostAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [dni, setDni] = useState("");

  // errores
  const [nombreError, setNombreError] = useState("");
  const [apellidosError, setApellidosError] = useState("");
  const [telefonoError, setTelefonoError] = useState("");
  const [codigoPostalError, setCodigoPostalError] = useState("");
  const [dniError, setDniError] = useState("");

  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [profileFile, setProfileFile] = useState<File | null>(null);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const stored = await SecureStorage.getUser();
      if (!stored) return;
      setUser(stored);

      const parts = (stored.fullName || "").split(" ");
      setFirstName(parts[0] || "");
      setLastName(parts.slice(1).join(" ") || "");

      // Debug: mostrar la fecha original y formateada
      console.log("Fecha original desde storage:", stored.dateOfBirth);
      const formattedDate = formatDateForInput(stored.dateOfBirth || "");
      console.log("Fecha formateada para input:", formattedDate);
      
      // Formatear la fecha para el input date (debe estar en formato YYYY-MM-DD)
      setDob(formattedDate);
      if (stored.sex === null || stored.sex === "NULL" || stored.sex === "") {
        setSex("NULL"); // Prefiero no decirlo
      } else {
        setSex(stored.sex);
      }
      setPhone(stored.phone || "");
      setPostAddress(stored.postAddress || "");
      setPostcode(stored.postcode || "");
      setDni(stored.dni || "");
      
      if (stored.profilePictureName) {
        setProfilePic(`${API_BASE}/api/profile_pic/${stored.profilePictureName}`);
      } else {
        setProfilePic(null);
      }
    })();
  }, []);

  async function pickImage() {
    if (Capacitor.getPlatform() === "web") {
      fileInputRef.current?.click();
    } else {
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
      });

      if (image.dataUrl) {
        setProfilePic(image.dataUrl);
        const res = await fetch(image.dataUrl);
        const blob = await res.blob();
        const file = new File([blob], `profile_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setProfileFile(file);
      }
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setProfileFile(file);
      setProfilePic(URL.createObjectURL(file));
    }
  }

  async function saveProfile() {
    if (saving) return; // evita doble click
    setSaving(true);

    try {
      if (!user) return;

      // validaciones antes de enviar
      const nErr = validateNombre(firstName);
      const aErr = validateApellidos(lastName);
      const tErr = validateTelefono(phone);
      const cpErr = validateCodigoPostal(postcode);
      const dErr = dni ? validateDni(dni) : "";
      setNombreError(nErr);
      setApellidosError(aErr);
      setTelefonoError(tErr);
      setCodigoPostalError(cpErr);
      setDniError(dErr);

      if (nErr || aErr || tErr || cpErr || dErr) {
        alert("⚠️ Corrige los errores antes de guardar.");
        return;
      }

      const updated = {
        id: user.id,
        email: user.email,
        fullName: `${firstName.trim()} ${lastName.trim()}`,
        dateOfBirth: formatDateForServer(dob), // Mantener fecha en formato YYYY-MM-DD para BD
        sex: sex || null,
        phone,
        postAddress,
        postcode,
        dni,
      };

      // Debug: mostrar la fecha antes de enviar
      console.log("Fecha desde input:", dob);
      console.log("Fecha para servidor (YYYY-MM-DD):", formatDateForServer(dob));

      const formData = new FormData();
      formData.append("user", JSON.stringify(updated));
      if (profileFile) {
        formData.append("profile_pic", profileFile);
      }

      await api.put("/api/mobile/user", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedUser = await fetchUserProfile(user.id);

      // Guardar en storage
      await SecureStorage.saveUser(updatedUser);
      setUser(updatedUser);
      alert("✅ Perfil actualizado");
      navigate(-1);
    } catch (err: any) {
      console.error("❌ Error actualizando:", err.response?.data || err.message || err);
      alert(err.response?.data?.error || "Error al actualizar perfil");
    } finally {
      setSaving(false);
    }
  }

  if (!user) return <p>Cargando…</p>;

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ textAlign: "center" }}>Editar perfil</h2>

      {/* Foto de perfil */}
      <div style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
        <div
          style={{
            width: 100,
            height: 100,
            borderRadius: "50%",
            overflow: "hidden",
            border: "2px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f9f9f9",
            cursor: "pointer",
          }}
          onClick={pickImage}
        >
          {profilePic ? (
            <img src={profilePic} alt="profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 24 }}>+</span>
          )}
        </div>
      </div>

      {/* input file oculto solo para web */}
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      <label>Email</label>
      <input type="email" value={user.email} disabled style={inputStyle} />

      <label>Nombre</label>
      <input
        type="text"
        value={firstName}
        onChange={(e) => {
          setFirstName(e.target.value);
          setNombreError(validateNombre(e.target.value));
        }}
        style={inputStyle}
      />
      {nombreError && <p style={{ color: "red" }}>{nombreError}</p>}

      <label>Apellidos</label>
      <input
        type="text"
        value={lastName}
        onChange={(e) => {
          setLastName(e.target.value);
          setApellidosError(validateApellidos(e.target.value));
        }}
        style={inputStyle}
      />
      {apellidosError && <p style={{ color: "red" }}>{apellidosError}</p>}

      <label>Fecha de nacimiento</label>
      <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} style={inputStyle} />

      <label>Sexo</label>
      <select value={sex} onChange={(e) => setSex(e.target.value)} style={inputStyle}>
        <option value="">Prefiero no decirlo</option>
        <option value="Male">Masculino</option>
        <option value="Female">Femenino</option>
      </select>

      <label>Teléfono</label>
      <input
        type="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setTelefonoError(validateTelefono(e.target.value));
        }}
        style={inputStyle}
      />
      {telefonoError && <p style={{ color: "red" }}>{telefonoError}</p>}

      <label>Dirección postal</label>
      <input type="text" value={postAddress} onChange={(e) => setPostAddress(e.target.value)} style={inputStyle} />

      <label>Código postal</label>
      <input
        type="number"
        value={postcode}
        onChange={(e) => {
          setPostcode(e.target.value);
          setCodigoPostalError(validateCodigoPostal(e.target.value));
        }}
        style={inputStyle}
      />
      {codigoPostalError && <p style={{ color: "red" }}>{codigoPostalError}</p>}

      <label>DNI</label>
      <input
        type="text"
        value={dni}
        onChange={(e) => {
          setDni(e.target.value);
          setDniError(validateDni(e.target.value));
        }}
        style={inputStyle}
      />
      {dniError && <p style={{ color: "red" }}>{dniError}</p>}

      <button
        onClick={saveProfile}
        style={{
          ...btnStyle,
          opacity: saving ? 0.6 : 1,
          cursor: saving ? "not-allowed" : "pointer",
        }}
        disabled={saving}
      >
        {saving ? "Guardando..." : "Guardar"}
      </button>
    </div>
  );
}


const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 10px",
  margin: "6px 0 12px",
  borderRadius: 8,
  border: "1px solid #ddd",
};

const btnStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 20,
  padding: "14px",
  background: "#1F67D1",
  color: "#fff",
  fontWeight: "bold",
  border: "none",
  borderRadius: 999,
  cursor: "pointer",
};